export interface ExponentialBackoffOptions {
  /** Wait used for the first retry; doubled on every attempt after that. */
  baseDelayMs: number
  /**
   * Fraction of the computed wait to randomise by, so `0.25` spreads a 4s wait
   * across 3s–5s. Defaults to no jitter.
   */
  jitterRatio?: number
  /** Ceiling the doubling is clamped to, applied before jitter. */
  maxDelayMs: number
}

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * Wait before retry number `attempt` (zero-based), doubling each time up to
 * `maxDelayMs`. Jitter spreads the retries out: when a service drops every
 * client at once, an unjittered backoff brings them all back in one burst and
 * knocks it over again.
 */
export const getExponentialBackoffDelay = (
  attempt: number,
  { baseDelayMs, jitterRatio = 0, maxDelayMs }: ExponentialBackoffOptions
): number => {
  const exponentialDelayMs = Math.min(
    maxDelayMs,
    baseDelayMs * 2 ** Math.max(0, attempt)
  )

  if (jitterRatio <= 0) {
    return Math.round(exponentialDelayMs)
  }

  const jitterWindowMs = exponentialDelayMs * Math.min(1, jitterRatio)
  const jitteredDelayMs =
    exponentialDelayMs - jitterWindowMs + Math.random() * jitterWindowMs * 2

  return Math.round(Math.max(0, jitteredDelayMs))
}
